"use client";

/**
 * WebBrowserOverlay
 *
 * Renders the WebBrowser as a full-screen overlay triggered by:
 *   - Custom event: `open-webbrowser` with `detail: { url: string }`
 *   - Or direct prop control via `isOpen` / `url`
 *
 * Usage from anywhere in the app:
 *   import { openWebBrowser } from "@/components/web-browser";
 *   openWebBrowser("https://example.com");
 */

import React, { useCallback, useEffect, useState } from "react";
import { WebBrowserProvider } from "@/contexts/web-browser-context";
import { WebBrowserWrapper } from "./WebBrowserWrapper";

// ── Helper to trigger the overlay from outside React ─────────────────────
export function openWebBrowser(url: string) {
	window.dispatchEvent(
		new CustomEvent("open-webbrowser", { detail: { url } }),
	);
}

// ── Overlay component (mounted once in providers.tsx) ────────────────────
export function WebBrowserOverlay() {
	const [isOpen, setIsOpen] = useState(false);
	const [url, setUrl] = useState("");

	useEffect(() => {
		function handleOpen(e: Event) {
			const detail = (e as CustomEvent<{ url: string }>).detail;
			if (detail?.url) {
				setUrl(detail.url);
				setIsOpen(true);
				document.body.style.overflow = "hidden";
			}
		}

		window.addEventListener("open-webbrowser", handleOpen);
		return () => {
			window.removeEventListener("open-webbrowser", handleOpen);
		};
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		setUrl("");
		document.body.style.overflow = "";
	}, []);

	if (!isOpen || !url) return null;

	return (
		<div
			className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50"
			onClick={(e) => {
				if (e.target === e.currentTarget) handleClose();
			}}
		>
			<div className="relative">
				<WebBrowserProvider initialUrl={url} onClose={handleClose}>
					<WebBrowserWrapper />
				</WebBrowserProvider>
			</div>
		</div>
	);
}
